/**
 * SITE CONTENT — the CMS behind the public pages.
 *
 * Every editable piece of the site is a BLOCK: a small JSON document with a
 * schema (below), a code DEFAULT (the content carried across from the
 * original Weebly site), and a spec the host portal's "Site content" screen
 * renders a form from. The Worker stores blocks opaquely in `site_content`
 * (workers/connect-api/src/handlers/content.ts); THIS file is the only place
 * that knows what a block looks like.
 *
 * Resolution on every read:
 *     saved block (GET /api/content)  merged over  the code default
 * field by field, then validated. A missing field, a malformed save or an
 * unreachable API all fall back to the default — so the site is never blank,
 * the prerenderer always has copy, and a host can never "break" a page by
 * clearing a field. Deleting a block's row (Reset in the portal) restores the
 * default wholesale.
 *
 * Adding a block: schema + default + spec here, then read it from a page with
 * `useBlock("key")`. No Worker change is needed.
 */
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  EMAIL,
  FACEBOOK_HANDLE,
  FACEBOOK_URL,
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
} from "./site-info";

/* ───────────────────────── admin-form specs ─────────────────────────── */

export type FieldKind = "text" | "multiline" | "image" | "url" | "date" | "select";

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Shown under the field in the portal. */
  help?: string;
  /** kind: "select" only. */
  options?: { value: string; label: string }[];
}

/** A repeatable group of fields (dates, locations, questions …). */
export interface ListSpec {
  key: string;
  label: string;
  /** Singular noun for the "Add …" button, e.g. "location". */
  itemLabel: string;
  help?: string;
  fields: FieldSpec[];
  /**
   * Field key holding an ISO date (YYYY-MM-DD). When set, the editor shows a
   * "Sort by date" button for the list and the weekday next to that field on
   * each row, so a typo'd date that isn't the day it claims to be is obvious.
   */
  dateField?: string;
}

export interface BlockSpec {
  key: BlockKey;
  title: string;
  description: string;
  fields: FieldSpec[];
  lists: ListSpec[];
}

/* ───────────────────────────── schemas ──────────────────────────────── */

const str = z.string().default("");
const link = z.object({ label: str, href: str });

export const Hero = z.object({
  eyebrow: str,
  title: str,
  tagline: str,
  intro: str,
  image: str,
  imageAlt: str,
  primaryLabel: str,
  primaryHref: str,
  secondaryLabel: str,
  secondaryHref: str,
});

export const Home = z.object({
  whatTitle: str,
  whatIntro: str,
  points: z.array(z.object({ label: str, detail: str })).default([]),
  whatImage: str,
  whatImageAlt: str,
  affiliationsTitle: str,
  affiliationsIntro: str,
  affiliationsList: str,
  affiliationsImage: str,
  affiliationsImageAlt: str,
  historyTitle: str,
  historyBody: str,
  historyImage: str,
  historyImageAlt: str,
});

export const Banners = z.object({
  sessionsImage: str,
  sessionsImageAlt: str,
  whatHappensImage: str,
  whatHappensImageAlt: str,
  campusTimeImage: str,
  campusTimeImageAlt: str,
  findUsImage: str,
  findUsImageAlt: str,
  faqImage: str,
  faqImageAlt: str,
  testimonialsImage: str,
  testimonialsImageAlt: str,
  uniGroupsImage: str,
  uniGroupsImageAlt: str,
  contactImage: str,
  contactImageAlt: str,
  registerImage: str,
  registerImageAlt: str,
  myImage: str,
  myImageAlt: str,
});

export const SeasonSessionKind = z.enum(["session", "campus", "break"]);

export const SeasonSession = z.object({
  /** ISO YYYY-MM-DD — always a Monday for Connect. */
  date: str,
  title: str,
  // `.catch()` rather than a hard failure: an empty or unrecognised kind (a
  // freshly-added row before its Type is chosen, or an old/foreign value)
  // falls back to "session" instead of failing the whole block's schema and
  // silently reverting every other saved edit — see resolveBlock below.
  kind: SeasonSessionKind.catch("session"),
  note: str,
});

export const Season = z.object({
  name: str,
  headline: str,
  time: str,
  dinnerNote: str,
  intro: str,
  sessions: z.array(SeasonSession).default([]),
  campusTimeNote: str,
  poster: str,
  posterAlt: str,
});

export const Locations = z.object({
  intro: str,
  items: z
    .array(
      z.object({
        region: str,
        short: str,
        church: str,
        address: str,
        mapUrl: str,
        image: str,
        imageAlt: str,
        note: str,
      }),
    )
    .default([]),
  campusTimeTitle: str,
  campusTimeChurch: str,
  campusTimeAddress: str,
  campusTimeMapUrl: str,
  campusTimeImage: str,
  campusTimeImageAlt: str,
  campusTimeNote: str,
});

export const WhatHappens = z.object({
  intro: str,
  topicsIntro: str,
  topics: z.array(z.object({ title: str })).default([]),
  image1: str,
  image1Alt: str,
  image2: str,
  image2Alt: str,
  campusTimeTitle: str,
  campusTimeBody: str,
  notUniNote: str,
});

export const CampusTime = z.object({
  intro: str,
  body: str,
  partnersTitle: str,
  partnersIntro: str,
  partners: z.array(z.object({ name: str, detail: str })).default([]),
});

export const Testimonials = z.object({
  featuredName: str,
  featuredRole: str,
  featuredQuote: str,
  featuredImage: str,
  intro: str,
  items: z.array(z.object({ name: str, quote: str, detail: str })).default([]),
});

export const Faq = z.object({
  intro: str,
  items: z.array(z.object({ question: str, answer: str })).default([]),
  moreTitle: str,
  moreBody: str,
});

export const UniGroups = z.object({
  intro: str,
  sections: z
    .array(
      z.object({
        title: str,
        body: str,
        link1Label: str,
        link1Href: str,
        link2Label: str,
        link2Href: str,
      }),
    )
    .default([]),
});

export const Contact = z.object({
  intro: str,
  email: str,
  instagramHandle: str,
  instagramUrl: str,
  facebookHandle: str,
  facebookUrl: str,
  formTitle: str,
  formIntro: str,
});

export const BLOCK_SCHEMAS = {
  hero: Hero,
  home: Home,
  banners: Banners,
  season: Season,
  locations: Locations,
  whatHappens: WhatHappens,
  campusTime: CampusTime,
  testimonials: Testimonials,
  faq: Faq,
  uniGroups: UniGroups,
  contact: Contact,
} as const;

export type BlockKey = keyof typeof BLOCK_SCHEMAS;
export type Block<K extends BlockKey> = z.infer<(typeof BLOCK_SCHEMAS)[K]>;
export type Blocks = { [K in BlockKey]: Block<K> };
export type NavLink = z.infer<typeof link>;

/* ───────────────────────────── defaults ─────────────────────────────── *
 * Carried across from connectsydneyministry.weebly.com (scanned 2026-09-14),
 * lightly tidied. Images live in public/images (ripped originals, optimised).
 * -------------------------------------------------------------------- */

export const DEFAULTS: Blocks = {
  hero: {
    eyebrow: "For Year 12 leavers across Sydney",
    title: "Connect Sydney",
    tagline: "Jesus | People | Campus",
    intro:
      "After the HSC, meet on Mondays to connect with Jesus through the Bible, connect with grads from other schools and churches, and connect with the Christian group at your uni before you get there.",
    image: "/images/hero-group.jpg",
    imageAlt: "The Connect Sydney crowd packed into a church hall, waving at the camera",
    primaryLabel: "Register now",
    primaryHref: "/register",
    secondaryLabel: "See the 2026/27 dates",
    secondaryHref: "/sessions",
  },
  home: {
    whatTitle: "What is Connect?",
    whatIntro: "The Connect ministry aims to help Year 12 leavers all over Sydney to:",
    points: [
      { label: "Connect with Jesus", detail: "through the Bible" },
      { label: "Connect with each other", detail: "from different schools and churches" },
      { label: "Connect with university campus groups", detail: "before semester one starts" },
    ],
    whatImage: "/images/what-is-connect.jpg",
    whatImageAlt: "A full room of Year 12 graduates at a Connect session",
    affiliationsTitle: "Our affiliations",
    affiliationsIntro:
      "Connect is connected to the AFES Christian groups at all major universities and gap year programs in NSW and Australia.",
    affiliationsList:
      "UNSW CBS, USYD EU, UTS Credo, MQ CU, WSU CBM, ACU EOC, The Bridge, Year 13 — as well as universities in the ACT, NT, QLD, TAS, VIC and WA.",
    affiliationsImage: "/images/campus-time-group.jpg",
    affiliationsImageAlt: "Grads and uni students talking in small groups at Campus Time",
    historyTitle: "Our history",
    historyBody:
      "Since 2009 we have assisted hundreds of Christians and their non-Christian friends transition from high school into university.\n\nWith operations in the North, South and West of Sydney, Connect continues today to preach God's word faithfully.",
    historyImage: "/images/our-history.jpg",
    historyImageAlt: "Four Connect leaders sitting together on a couch, laughing",
  },
  banners: {
    sessionsImage: "/images/banner-sessions.jpg",
    sessionsImageAlt: "A Connect leader giving a talk to grads in Connect t-shirts, seated in a church hall",
    whatHappensImage: "/images/banner-what-happens.jpg",
    whatHappensImageAlt: "Grads listening intently during a talk at Connect",
    campusTimeImage: "/images/banner-campus-time.jpg",
    campusTimeImageAlt: "Grads sitting together in a circle at Campus Time",
    findUsImage: "/images/banner-find-us.jpg",
    findUsImageAlt: "Grads sitting on the floor of a church hall at Connect, smiling and waving at the camera",
    faqImage: "/images/banner-faq.jpg",
    faqImageAlt: "Two leaders giving a talk with slides to grads seated in a church hall",
    testimonialsImage: "/images/banner-testimonials.jpg",
    testimonialsImageAlt: "Three grads grinning at the camera, giving thumbs up",
    uniGroupsImage: "/images/banner-uni-groups.jpg",
    uniGroupsImageAlt: "A leader chatting with a circle of grads sitting on the grass",
    contactImage: "/images/banner-contact.jpg",
    contactImageAlt: "A group of smiling grads sitting together on the lawn, some making peace signs",
    registerImage: "/images/banner-register.jpg",
    registerImageAlt: "A hall full of grads at a combined Connect session, with leaders in Connect t-shirts at the sound desk",
    myImage: "/images/what-happens.jpg",
    myImageAlt: "Grads and leaders chatting in small groups around tables in a church hall",
  },
  season: {
    name: "2026/27",
    headline: "HSC done? Come to Connect.",
    time: "4–6pm",
    dinnerNote: "Dinner afterwards is optional — bring a few dollars if you'd like to stay.",
    intro:
      "Every Monday after the HSC, at three locations across Sydney. Come to whichever is closest — most are a short walk from the train station.",
    sessions: [
      { date: "2026-11-09", title: "Connect 1", kind: "session", note: "" },
      { date: "2026-11-16", title: "Connect 2", kind: "session", note: "" },
      { date: "2026-11-23", title: "Connect 3", kind: "session", note: "" },
      { date: "2026-11-30", title: "Connect 4", kind: "session", note: "" },
      { date: "2026-12-07", title: "Break", kind: "break", note: "" },
      { date: "2026-12-14", title: "Connect 5", kind: "session", note: "" },
      { date: "2026-12-21", title: "Connect 6", kind: "session", note: "" },
      { date: "2026-12-28", title: "Break", kind: "break", note: "" },
      { date: "2027-01-04", title: "Break", kind: "break", note: "" },
      { date: "2027-01-11", title: "Connect 7", kind: "session", note: "" },
      { date: "2027-01-18", title: "Connect 8", kind: "session", note: "" },
      { date: "2027-01-25", title: "Connect 9", kind: "campus", note: "Wynyard — venue TBC" },
      { date: "2027-02-01", title: "Connect 10", kind: "campus", note: "Wynyard — venue TBC" },
      { date: "2027-02-08", title: "Connect 11", kind: "campus", note: "Wynyard — venue TBC" },
    ],
    campusTimeNote:
      "The final three sessions (25 January, 1 February and 8 February) combine near Wynyard for Campus Time — venue to be confirmed.",
    // 2026/27 poster made for the rebuilt site (the 2025/26 one is kept at
    // /images/poster-2025-26.png for reference). Clear it to show the summary card.
    poster: "/images/poster-2026-27.jpg",
    posterAlt: "Connect 2026/27 poster: HSC done? Come to Connect. Starts Monday 9 November 2026, Mondays 4–6pm with dinner optional, North, South and West, with Campus Time near Wynyard on 25 January, 1 and 8 February 2027",
  },
  locations: {
    intro:
      "Three locations, one program. Come to the one closest to you — each is a short walk from the station, with parking nearby.",
    items: [
      {
        region: "Connect North",
        short: "N",
        church: "Eastwood Community Baptist Church",
        address: "3 First Ave, Eastwood NSW 2122",
        mapUrl: "https://maps.app.goo.gl/3VaiJSTN1VyLq4fh7",
        image: "/images/location-north.jpg",
        imageAlt: "Eastwood Community Baptist Church",
        note: "Near Eastwood train station",
      },
      {
        region: "Connect South",
        short: "S",
        church: "St George's Hurstville Anglican Church",
        address: "2 The Avenue, Hurstville NSW 2220",
        mapUrl: "https://maps.app.goo.gl/5EWSdhTfHkzpdS1s6",
        image: "/images/location-south.jpg",
        imageAlt: "St George's Hurstville Anglican Church",
        note: "Near Hurstville train station",
      },
      {
        region: "Connect West",
        short: "W",
        church: "Cabramatta Anglican Church",
        address: "42 Cumberland St, Cabramatta NSW 2166",
        mapUrl: "https://maps.app.goo.gl/nNj1CLsgVSyTeoHi8",
        image: "/images/location-west.jpg",
        imageAlt: "Cabramatta Anglican Church",
        note: "Near Cabramatta train station",
      },
    ],
    campusTimeTitle: "Campus Time 2027",
    campusTimeChurch: "Wynyard — venue TBC",
    campusTimeAddress: "Near Wynyard Station, Sydney NSW 2000",
    campusTimeMapUrl: "https://www.google.com/maps/search/?api=1&query=Wynyard+Station%2C+Sydney+NSW",
    // No confirmed venue yet — the St Philip's photo would claim a venue
    // nobody has confirmed, so this stays blank until Campus Time has one.
    campusTimeImage: "",
    campusTimeImageAlt: "",
    campusTimeNote: "All three locations combine near Wynyard for the final three sessions — venue to be confirmed.",
  },
  whatHappens: {
    intro:
      "After the HSC finishes, we meet on Mondays to connect with each other through Bible talks, workshops, games, food and more!",
    topicsIntro: "We have covered a vast array of topics through the Bible talks and workshops, such as:",
    topics: [
      { title: "Biblical historicity" },
      { title: "Predestination" },
      { title: "Standing firm in the faith" },
      { title: "Don't waste your life" },
    ],
    image1: "/images/what-happens-notes.jpg",
    image1Alt: "A grad holding printed talk notes on their lap during a session",
    image2: "/images/what-happens-circle.jpg",
    image2Alt: "Grads in Connect t-shirts sitting in a big circle, talking together in a hall",
    campusTimeTitle: "Campus Time",
    campusTimeBody:
      "The first eight Connect sessions run 4–6pm at the North, South and West locations. For the final three weeks, we combine for Campus Time — where you'll be split into your university Christian groups to meet friends and find out more about your campus.",
    notUniNote:
      "If you're not heading to university (gap year, college, work), still come along to meet solid friends and learn how to live the rest of your life for God!",
  },
  campusTime: {
    intro:
      "Campus Time is where you meet people heading to the same uni as you, to build new relationships with people who might even end up as lifelong friends.",
    body:
      "Uni Christian group staff workers will also attend to answer any questions about uni life and Christian groups.\n\nGrads who aren't heading to uni should still come to be encouraged by the talk, hang out with like-minded people and make lifelong friendships.",
    partnersTitle: "Who we partner with",
    partnersIntro:
      "Campus Time is run with the AFES-affiliated Christian groups on Sydney's campuses and the gap-year programs many grads join.",
    partners: [
      { name: "CBS", detail: "Campus Bible Study — UNSW" },
      { name: "EU", detail: "Evangelical Union — University of Sydney" },
      { name: "Credo", detail: "UTS" },
      { name: "CU", detail: "Christian Union — Macquarie University" },
      { name: "CBM", detail: "Campus Bible Ministry — Western Sydney University" },
      { name: "EOC", detail: "ACU" },
      { name: "The Bridge", detail: "Gap year program" },
      { name: "Year 13", detail: "Gap year program" },
    ],
  },
  testimonials: {
    featuredName: "Peter Ko",
    featuredRole: "Lead Pastor at Bankstown Anglican Church · 2023 parent of a Year 12 grad · 2024 speaker at Connect South",
    featuredQuote:
      "Connect is an incredibly important ministry. Statistics show that we lose so many youth from the faith in the transition from high school to tertiary studies. Connect ensures that the first step they make post-HSC is towards growth and fellowship with other graduates, led by university students, with a view of connecting them with each other and campus ministries. I don't think there is a more important group churches and parents should be strongly encouraging their graduates to participate in.",
    featuredImage: "/images/peter-ko.jpg",
    intro: "In their own words — grads who came to Connect and what it meant for their first year at uni.",
    items: [
      {
        name: "Gary Liu",
        quote:
          "Found it really helpful in understanding what sorts of university ministry different unis have and how they run. Also really cool to connect with older upper years who also go to the uni I was planning to enrol in — I later ended up reading the Bible with one of them during my first year. It was also cool to get to know other people from different schools and going to different unis and spend time together at Connect during the event and after during dinner. I found the talks really informative as well, especially tackling some of the questions of Christianity when going into adulthood — jobs, relationships, evangelism in uni and the workplace. One talk that stood out was the historicity talk with Frank, in which I really got an in-depth understanding of the Bible as a historical source and how all the people and events in the Bible link up.",
        detail: "St George North Anglican Church · Campus Bible Study (CBS) at UNSW · Bachelor of Commerce / Computer Science",
      },
      {
        name: "Jason Solanki",
        quote:
          "I had a great time meeting lots of new people who I have continued to be friends with at uni. It helped me to be connected to uni Christian groups and dealt with my boredom post-HSC. And most importantly, I learnt a lot about what living for Jesus looks like.",
        detail: "Hurstville Presbyterian Church · Campus Bible Study (CBS) at UNSW · Mechatronic Engineering",
      },
      {
        name: "Tom McDougall",
        quote:
          "Connect was extremely encouraging as it really opened my eyes to how many Christians there were who are my age and with similar interests! The friends I met made starting uni a lot easier and we've kept in contact ever since. Connect is such an easy thing to take part in, but the benefits you get are so much more than you'd expect.",
        detail: "Merrylands East Presbyterian Church · Credo at UTS · Bachelor of Sport and Exercise Science",
      },
      {
        name: "Alexander Tan",
        quote:
          "Many of the uni friends I made I first met at Connect (particularly in first year) — it really helped me get to settle into the uni Christian group well. I really enjoyed the social aspect, meeting with people in a similar life stage and being encouraged by speakers to live out our faith as Christians given the new freedoms we have as uni students. Overall a great place to hang and meet Christians or explore Christianity with people going through a similar stage of life — also it's a really nice community of people to hang with who you might see on campus.",
        detail: "Arise Anglican Church · CBS at UNSW · Software Engineering",
      },
      {
        name: "Andrew Lee",
        quote:
          "It was fun getting to meet new people and getting to know God better through the sermons that different pastors have preached.",
        detail: "SYNC · SOW MQ · Mechanical Engineering at Macquarie University",
      },
      {
        name: "Anonymous",
        quote:
          "I really enjoyed my time at Connect! I met a lot of new friends and learned so much about God's character. We also covered a variety of topics from the Bible. Some of the talks from the guest speakers were controversial, but all of them helped me approach the Bible from a different perspective and grow in faith! I loved the dinner chats we had after Connect as well. Sometimes we played frisbee at night and stayed back for so long that we'd arrive home before midnight. Whether you are a Christian or someone who is curious and wants to know more about Christianity, I'd definitely recommend Connect for you. Come along!",
        detail: "Pennant Hills Baptist Church · Psychology at USYD",
      },
      {
        name: "Anonymous",
        quote:
          "Great for meeting and getting to know new Christians as well as being connected to your uni Christian group.",
        detail: "",
      },
      {
        name: "Anonymous",
        quote:
          "I'm so glad I went to Connect as a grad! To be honest I was pretty anxious going the first few times because I didn't know many people, but I was always welcomed by everyone (other grads and leaders!) and I always left feeling energised from spending time with other young Christians and having quality discussions about God's Word.",
        detail: "",
      },
      {
        name: "Anonymous",
        quote:
          "My time at Connect gave me the perfect opportunity to think about my faith and how we can honour God as we move onto the next stages of our lives. Not only were the talks and discussion groups engaging and thought-provoking, but the fellowship with other post-HSC students proved to be helpful as we started university and got connected with our respective Christian uni groups. I look back at Connect as a short but memorable time where I got to meet a lot of new people and learn about how I would like to grow and cultivate my personal relationship with God.",
        detail: "",
      },
    ],
  },
  faq: {
    intro: "The questions we get asked most. Not answered here? Send us a message.",
    items: [
      {
        question: "Who is Connect for?",
        answer:
          "Connect is for Year 12 graduates over their summer break between high school and work or tertiary education, to connect them to Jesus, people and university campuses and set them up for their next stage in life.",
      },
      {
        question: "What do I bring?",
        answer:
          "There is nothing you need to bring, but money (or some way to pay) would be needed if you want to buy dinner. A Bible and a pen can also be helpful if you own one. And bring a friend :)",
      },
      {
        question: "How do I get there?",
        answer:
          "Depending on where you live, we have multiple locations where we run Connect — but it is usually a short walk from the train station to the church. We have posts on our Instagram about getting to certain locations by public transport and on parking instructions.",
      },
      {
        question: "What's the deal with dinner?",
        answer:
          "After our formal session (4–6pm), we have an optional dinner time where we will usually have dinner at the church or go out to restaurants in the area to help foster further discussion and connections. It is recommended that you set aside more time to stay for this and bring money to pay, since it is a great time to connect. Dinner ends as late as you want it to end, since it's not part of the formal program.",
      },
      {
        question: "What if I'm not going to uni?",
        answer:
          "Still come!! We welcome all Year 12 graduates, regardless of whether you are going to uni. It can still be a valuable event to connect to Jesus and people, which can hopefully build lasting relationships for the rest of life.",
      },
      {
        question: "Doing a gap year?",
        answer:
          "We have some leaders who have been on gap year programs — Year 13 and The Bridge — before, so come and ask them some questions. Even if you're just doing your own thing it's still a great opportunity to connect to Jesus, people and campuses, so when you go to uni you will have people and groups you know, and have these relationships for the rest of life.",
      },
      {
        question: "What's Connect like?",
        answer:
          "We have a two-hour formal program, which usually entails two sessions to hear from the Bible to learn about Jesus and connect to each other. In the last three weeks we have Campus Time, where we get to connect to the Christian groups in our university. We then have an informal dinner, where we have the opportunity to connect to each other over a meal and stay indefinitely for conversation.",
      },
      {
        question: "Not a Christian?",
        answer:
          "Still come!! We welcome all Year 12 graduates and we believe that knowing Jesus Christ is the most important knowledge that anyone can know. Whether you are interested to find out more about who Jesus is or in just meeting people, we would be overjoyed to meet you.",
      },
    ],
    moreTitle: "More questions?",
    moreBody: "Send us a message on our socials or through email and we will get in touch.",
  },
  uniGroups: {
    intro:
      "Get in touch with the Christian group at your university before you arrive — and find out about the camps and events that happen around the year.",
    sections: [
      {
        title: "Heading to uni",
        body: "Everything a Year 12 leaver needs to know about starting uni as a Christian, and a directory of every campus group.",
        link1Label: "Heading to Uni information",
        link1Href: "https://www.headingtouni.info/",
        link2Label: "Find your Christian group",
        link2Href: "https://www.headingtouni.info/findmygroup2023",
      },
      {
        title: "National camp — NTE",
        body: "Students from any university (and Year 12s!) are welcome at the National Training Event, and each Christian uni group will have at least one additional camp during the year as well — usually called MYC, the Mid-Year Conference.",
        link1Label: "nte.org.au",
        link1Href: "https://nte.org.au/",
        link2Label: "NTE on Instagram",
        link2Href: "https://www.instagram.com/afesnte/",
      },
      {
        title: "AFES — Australian Fellowship of Evangelical Students",
        body: "The national movement behind the Christian groups on Australian campuses. Their site lists every affiliated group.",
        link1Label: "AFES website",
        link1Href: "https://afes.org.au/",
        link2Label: "Find your uni group",
        link2Href: "https://afes.org.au/find-my-uni-group/",
      },
      {
        title: "Meet Jesus",
        body: "As we partner together for Meet Jesus, there will be events organised near you. We went through the gospel of John in light of Meet Jesus in 2024 semester one — see Uncover for the material.",
        link1Label: "meetjesus.au",
        link1Href: "https://meetjesus.au/",
        link2Label: "uncover.org.au",
        link2Href: "https://uncover.org.au/",
      },
    ],
  },
  contact: {
    intro:
      "Questions, feedback, or keen to come along? Message us on social media, email us, or use the form below.",
    email: EMAIL,
    instagramHandle: INSTAGRAM_HANDLE,
    instagramUrl: INSTAGRAM_URL,
    facebookHandle: FACEBOOK_HANDLE,
    facebookUrl: FACEBOOK_URL,
    formTitle: "Contact form",
    formIntro: "For expressions of interest, enquiries and feedback. We'll reply by email.",
  },
};

/* ───────────────────────── portal form specs ────────────────────────── */

const img = (key: string, label: string, help?: string): FieldSpec => ({ key, label, kind: "image", help });
const text = (key: string, label: string, help?: string): FieldSpec => ({ key, label, kind: "text", help });
const multi = (key: string, label: string, help?: string): FieldSpec => ({ key, label, kind: "multiline", help });
const url = (key: string, label: string, help?: string): FieldSpec => ({ key, label, kind: "url", help });
const date = (key: string, label: string, help?: string): FieldSpec => ({ key, label, kind: "date", help });
const select = (
  key: string,
  label: string,
  options: { value: string; label: string }[],
  help?: string,
): FieldSpec => ({ key, label, kind: "select", options, help });

const SESSION_KIND_OPTIONS = [
  { value: "session", label: "Connect session" },
  { value: "campus", label: "Campus Time" },
  { value: "break", label: "Break" },
];

export const BLOCK_SPECS: BlockSpec[] = [
  {
    key: "hero",
    title: "Home — banner",
    description: "The big photo and headline at the top of the home page, and its two buttons.",
    fields: [
      text("eyebrow", "Small line above the title"),
      text("title", "Title"),
      text("tagline", "Tagline", "Shown under the title, e.g. Jesus | People | Campus"),
      multi("intro", "Intro paragraph"),
      img("image", "Background photo", "Landscape works best — at least 1600px wide."),
      text("imageAlt", "Photo description", "Read out by screen readers; describe what is in the photo."),
      text("primaryLabel", "Main button label"),
      url("primaryHref", "Main button link", "A page on this site (e.g. /sessions) or a full URL."),
      text("secondaryLabel", "Second button label"),
      url("secondaryHref", "Second button link"),
    ],
    lists: [],
  },
  {
    key: "home",
    title: "Home — sections",
    description: "What is Connect, Our affiliations and Our history.",
    fields: [
      text("whatTitle", "What is Connect — heading"),
      multi("whatIntro", "What is Connect — intro"),
      img("whatImage", "What is Connect — photo"),
      text("whatImageAlt", "Photo description"),
      text("affiliationsTitle", "Affiliations — heading"),
      multi("affiliationsIntro", "Affiliations — intro"),
      multi("affiliationsList", "Affiliations — the list"),
      img("affiliationsImage", "Affiliations — photo"),
      text("affiliationsImageAlt", "Photo description"),
      text("historyTitle", "History — heading"),
      multi("historyBody", "History — text", "Leave a blank line between paragraphs."),
      img("historyImage", "History — photo"),
      text("historyImageAlt", "Photo description"),
    ],
    lists: [
      {
        key: "points",
        label: "The three 'Connect with …' points",
        itemLabel: "point",
        fields: [text("label", "Bold part"), text("detail", "Rest of the line")],
      },
    ],
  },
  {
    key: "banners",
    title: "Page banners",
    description:
      "The photo at the top of each inner page. Leave a photo empty to show the plain heading band instead.",
    fields: [
      img("sessionsImage", "Sessions — photo"),
      text("sessionsImageAlt", "Photo description"),
      img("whatHappensImage", "What happens — photo"),
      text("whatHappensImageAlt", "Photo description"),
      img("campusTimeImage", "Campus Time — photo"),
      text("campusTimeImageAlt", "Photo description"),
      img("findUsImage", "Find us — photo"),
      text("findUsImageAlt", "Photo description"),
      img("faqImage", "FAQ — photo"),
      text("faqImageAlt", "Photo description"),
      img("testimonialsImage", "Testimonials — photo"),
      text("testimonialsImageAlt", "Photo description"),
      img("uniGroupsImage", "Uni Christian groups — photo"),
      text("uniGroupsImageAlt", "Photo description"),
      img("contactImage", "Contact — photo"),
      text("contactImageAlt", "Photo description"),
      img("registerImage", "Register — photo"),
      text("registerImageAlt", "Photo description"),
      img("myImage", "My registrations — photo"),
      text("myImageAlt", "Photo description"),
    ],
    lists: [],
  },
  {
    key: "season",
    title: "This season — sessions & poster",
    description:
      "The Sessions page: every date this season, whether it's a Connect session, Campus Time or a break, and the poster.",
    fields: [
      text("name", "Season name", "e.g. 2026/27"),
      text("headline", "Headline"),
      text("time", "Time", "e.g. 4–6pm"),
      text("dinnerNote", "Dinner note"),
      multi("intro", "Intro"),
      multi("campusTimeNote", "Campus Time note", "Which sessions combine, and where."),
      img("poster", "Poster image", "Leave blank to hide the poster — the page uses the space for a summary instead."),
      text("posterAlt", "Poster description"),
    ],
    lists: [
      {
        key: "sessions",
        label: "Sessions (Mondays)",
        itemLabel: "session",
        help: "Every Monday this season, in order — a Connect session, the combined Campus Time sessions, or a week off.",
        dateField: "date",
        fields: [
          date("date", "Date"),
          text("title", "Title", "e.g. Connect 1"),
          select("kind", "Type", SESSION_KIND_OPTIONS),
          text("note", "Note", "Shown for Campus Time, e.g. Wynyard — venue TBC"),
        ],
      },
    ],
  },
  {
    key: "locations",
    title: "Find us — locations",
    description: "The three Connect locations and the Campus Time venue, with map links and photos.",
    fields: [
      multi("intro", "Intro"),
      text("campusTimeTitle", "Campus Time — heading"),
      text("campusTimeChurch", "Campus Time — venue"),
      text("campusTimeAddress", "Campus Time — address"),
      url("campusTimeMapUrl", "Campus Time — Google Maps link"),
      img("campusTimeImage", "Campus Time — photo", "Leave blank until the venue is confirmed."),
      text("campusTimeImageAlt", "Photo description"),
      text("campusTimeNote", "Campus Time — note"),
    ],
    lists: [
      {
        key: "items",
        label: "Locations",
        itemLabel: "location",
        fields: [
          text("region", "Name", "e.g. Connect North"),
          text(
            "short",
            "Short name",
            'The abbreviation used in summaries, the footer and the registration picker \u2014 e.g. "N" or "NW". Leave blank to use the first letter of the name.',
          ),
          text("church", "Church"),
          text("address", "Address"),
          url("mapUrl", "Google Maps link"),
          img("image", "Photo"),
          text("imageAlt", "Photo description"),
          text("note", "Note", "e.g. Near Eastwood train station"),
        ],
      },
    ],
  },
  {
    key: "whatHappens",
    title: "What happens at Connect",
    description: "The programme page: what a Monday looks like, talk topics and Campus Time.",
    fields: [
      multi("intro", "Intro"),
      text("topicsIntro", "Topics — lead-in line"),
      img("image1", "Photo 1"),
      text("image1Alt", "Photo 1 description"),
      img("image2", "Photo 2"),
      text("image2Alt", "Photo 2 description"),
      text("campusTimeTitle", "Campus Time — heading"),
      multi("campusTimeBody", "Campus Time — text"),
      multi("notUniNote", "Not heading to uni — note"),
    ],
    lists: [
      {
        key: "topics",
        label: "Talk & workshop topics",
        itemLabel: "topic",
        fields: [text("title", "Topic")],
      },
    ],
  },
  {
    key: "campusTime",
    title: "Campus Time page",
    description: "What Campus Time is and who we partner with.",
    fields: [
      multi("intro", "Intro"),
      multi("body", "Text", "Leave a blank line between paragraphs."),
      text("partnersTitle", "Partners — heading"),
      multi("partnersIntro", "Partners — intro"),
    ],
    lists: [
      {
        key: "partners",
        label: "Partner groups",
        itemLabel: "group",
        fields: [text("name", "Short name", "e.g. CBS"), text("detail", "Full name / campus")],
      },
    ],
  },
  {
    key: "testimonials",
    title: "Testimonials",
    description: "The featured quote on the home page and the student testimonials page.",
    fields: [
      text("featuredName", "Featured — name"),
      text("featuredRole", "Featured — role"),
      multi("featuredQuote", "Featured — quote"),
      img("featuredImage", "Featured — photo"),
      multi("intro", "Testimonials page — intro"),
    ],
    lists: [
      {
        key: "items",
        label: "Student testimonials",
        itemLabel: "testimonial",
        fields: [
          text("name", "Name", "Use 'Anonymous' to withhold it."),
          multi("quote", "Quote"),
          text("detail", "Church · uni group · degree"),
        ],
      },
    ],
  },
  {
    key: "faq",
    title: "FAQ",
    description: "Questions and answers, plus the 'more questions' footer.",
    fields: [multi("intro", "Intro"), text("moreTitle", "More questions — heading"), multi("moreBody", "More questions — text")],
    lists: [
      {
        key: "items",
        label: "Questions",
        itemLabel: "question",
        fields: [text("question", "Question"), multi("answer", "Answer")],
      },
    ],
  },
  {
    key: "uniGroups",
    title: "Uni Christian groups",
    description: "Links to the campus groups, camps and partner ministries.",
    fields: [multi("intro", "Intro")],
    lists: [
      {
        key: "sections",
        label: "Sections",
        itemLabel: "section",
        fields: [
          text("title", "Heading"),
          multi("body", "Text"),
          text("link1Label", "Link 1 — label"),
          url("link1Href", "Link 1 — URL"),
          text("link2Label", "Link 2 — label"),
          url("link2Href", "Link 2 — URL"),
        ],
      },
    ],
  },
  {
    key: "contact",
    title: "Contact",
    description: "Email, social handles and the contact-form wording. Also feeds the footer.",
    fields: [
      multi("intro", "Intro"),
      text("email", "Email address"),
      text("instagramHandle", "Instagram handle"),
      url("instagramUrl", "Instagram URL"),
      text("facebookHandle", "Facebook page name"),
      url("facebookUrl", "Facebook URL"),
      text("formTitle", "Form — heading"),
      multi("formIntro", "Form — intro"),
    ],
    lists: [],
  },
];

/* ───────────────────────────── resolution ───────────────────────────── */

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Saved value over the default, field by field. Strings win when they are
 * strings; a list wins when it is an array (each item filled out from the
 * default's first item so a half-saved row never loses a key); anything else
 * keeps the default. Then the schema has the final say.
 */
export function resolveBlock<K extends BlockKey>(key: K, saved: unknown): Block<K> {
  const def = DEFAULTS[key] as Record<string, unknown>;
  if (!isRecord(saved)) return DEFAULTS[key];
  const merged: Record<string, unknown> = { ...def };
  for (const [field, defValue] of Object.entries(def)) {
    const v = saved[field];
    if (typeof defValue === "string") {
      if (typeof v === "string") merged[field] = v;
    } else if (Array.isArray(defValue)) {
      if (Array.isArray(v)) {
        const template = isRecord(defValue[0])
          ? Object.fromEntries(Object.keys(defValue[0]).map((k) => [k, ""]))
          : {};
        merged[field] = v.filter(isRecord).map((item) => ({ ...template, ...item }));
      }
    }
  }
  const parsed = BLOCK_SCHEMAS[key].safeParse(merged);
  return (parsed.success ? parsed.data : DEFAULTS[key]) as Block<K>;
}

/** Vite proxies `/api` to the Worker in dev; a relative base is correct. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

async function fetchBlocks(): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/api/content`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`GET /api/content → ${res.status}`);
  const data = (await res.json()) as { blocks?: unknown };
  return isRecord(data.blocks) ? data.blocks : {};
}

/** Every saved block (raw). Shared by all pages via react-query's cache. */
export function useSavedBlocks() {
  return useQuery({
    queryKey: ["site-content"],
    queryFn: fetchBlocks,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * One block, resolved. Returns the code default until the API answers (and
 * if it never does), so a page always has something to render — including
 * under the prerenderer and for a visitor whose request to the API failed.
 */
export function useBlock<K extends BlockKey>(key: K): Block<K> {
  const { data } = useSavedBlocks();
  return resolveBlock(key, data?.[key]);
}

/** Split CMS multiline text into paragraphs on blank lines. */
export function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/* ─────────────────────────── locations helpers ──────────────────────────── *
 * The host owns BOTH the full name ("Connect North") and its abbreviation
 * ("N"), so every summary, footer line and registration option follows a
 * rename. Nothing derives an abbreviation by mangling the name any more.
 * -------------------------------------------------------------------- */

export type LocationItem = Block<"locations">["items"][number];

/** The always-available answer to "Which location will you attend?" on
 *  /register — mirrored as LOCATION_UNSURE in the Worker
 *  (workers/connect-api/src/handlers/registrations.ts), which validates it. */
export const LOCATION_UNSURE = "Not sure yet";

/**
 * The host's abbreviation for a location. A blank one falls back to the first
 * letter of the name (minus the "Connect " prefix), and finally to the name
 * itself — so a cleared field never renders an empty chip.
 */
export function locationShort(l: Pick<LocationItem, "region" | "short">): string {
  const short = l.short.trim();
  if (short) return short;
  const name = l.region.trim().replace(/^Connect\s+/i, "");
  return name ? name.charAt(0).toUpperCase() : l.region.trim();
}

/** "N · S · W" — the abbreviations, as the summaries and the footer show them. */
export function locationShortList(items: LocationItem[]): string {
  return items.map((l) => locationShort(l)).filter(Boolean).join(" · ");
}

/** "Connect North, Connect South and Connect West" — the full names, in prose. */
export function locationNames(items: LocationItem[]): string {
  const names = items.map((l) => l.region.trim()).filter(Boolean);
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
}
